import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, InputBase, Avatar, Typography, Chip } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { tokens, gradientForName } from '../theme/tokens';
import { OFFICE_HOME, OFFICE_MEMBERS, OFFICE_SETTINGS, CASE_INFO } from '../routes/routes';
import { useCases } from '../store/casesContext';
import { askOfficeCopilot } from '../hooks/useOfficeCopilot';

const buildPath = (pattern, params) =>
  Object.entries(params).reduce((acc, [key, val]) => acc.replace(`:${key}`, val), pattern);

const getMemberName = (member) => {
  if (!member) return null;
  return member.person?.full_name?.replace(';', '') || member.name_str || null;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0]?.substring(0, 2).toUpperCase() || '?';
};

const SearchPaletteModal = ({ open, onClose, officeId }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { cases } = useCases();
  const [query, setQuery] = useState('');
  const [aiAnswer, setAiAnswer] = useState(null);

  const SUGGESTED_PROMPTS = [
    '¿Qué expedientes tienen plazos esta semana?',
    '¿Cuántos expedientes están en trámite?',
  ];

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleAskAI = async (question) => {
    setAiAnswer({ question, text: '', loading: true, error: '' });
    try {
      const reply = await askOfficeCopilot(officeId, question);
      setAiAnswer({ question, text: reply, loading: false, error: '' });
    } catch (err) {
      setAiAnswer({ question, text: '', loading: false, error: err.message });
    }
  };

  const go = (pattern) => {
    navigate(buildPath(pattern, { officeId }));
    onClose();
  };

  const results = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return cases.filter(exp => {
      const matchesBasic =
        exp.title?.toLowerCase().includes(q) ||
        exp.number?.toLowerCase().includes(q);
      const matchesClient = getMemberName(exp.members?.client)?.toLowerCase().includes(q);
      const matchesLawyer = getMemberName(exp.members?.lawyer)?.toLowerCase().includes(q);
      const matchesPartners = exp.members?.partners?.some(p => getMemberName(p)?.toLowerCase().includes(q));
      return matchesBasic || matchesClient || matchesLawyer || matchesPartners;
    }).slice(0, 8);
  })();

  const navItems = [
    { label: 'Panel del despacho', icon: DashboardRoundedIcon, action: () => go(OFFICE_HOME) },
    { label: 'Miembros del despacho', icon: GroupRoundedIcon, action: () => go(OFFICE_MEMBERS) },
    { label: 'Configuración', icon: SettingsRoundedIcon, action: () => go(OFFICE_SETTINGS) },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(620px, 92vw)',
          borderRadius: `${tokens.radius.lg}px`,
          border: `1px solid ${tokens.colors.line2}`,
          boxShadow: '0 30px 80px rgba(20,24,45,.22)',
          mt: '12vh',
          alignSelf: 'flex-start',
        },
      }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: `1px solid ${tokens.colors.line}` }}>
        <SearchRoundedIcon sx={{ color: tokens.colors.cyan }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setAiAnswer(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim() && results.length === 0) {
              handleAskAI(query.trim());
            }
          }}
          placeholder="Busca expedientes, clientes o pregunta a la IA…"
          fullWidth
          sx={{ fontSize: 16, color: tokens.colors.txt }}
        />
      </Box>

      <Box sx={{ p: 1.25, maxHeight: '55vh', overflowY: 'auto' }}>
        {query.trim() ? (
          results.length > 0 ? (
            <>
              <Box sx={{ fontSize: 11, color: tokens.colors.muted2, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', px: 1.25, pt: 1, pb: 0.5 }}>
                Expedientes
              </Box>
              {results.map(exp => {
                const clientName = getMemberName(exp.members?.client) || 'Sin cliente';
                return (
                  <Box
                    key={exp.id}
                    onClick={() => go(CASE_INFO.replace(':officeId', officeId).replace(':caseId', exp.id))}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
                      borderRadius: `${tokens.radius.md}px`, cursor: 'pointer',
                      '&:hover': { backgroundColor: tokens.colors.panel2 },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32, height: 32, fontSize: 12, fontWeight: 700,
                        backgroundImage: gradientForName(clientName), color: '#0a0b10',
                      }}
                    >
                      {getInitials(clientName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.colors.txt }} noWrap>
                        {exp.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: tokens.colors.muted }} noWrap>
                        {exp.number} · {clientName}
                      </Typography>
                    </Box>
                    <Chip
                      label={exp.state === 'completed' ? 'Finalizado' : 'En trámite'}
                      size="small"
                      sx={{
                        fontSize: 11, fontWeight: 650,
                        backgroundColor: exp.state === 'completed' ? 'rgba(31,157,99,.14)' : 'rgba(229,181,103,.16)',
                        color: exp.state === 'completed' ? tokens.colors.ok : tokens.colors.gold,
                      }}
                    />
                  </Box>
                );
              })}
            </>
          ) : aiAnswer ? (
            <Box sx={{ px: 1.5, py: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 15, color: tokens.colors.cyan }} />
                <Typography sx={{ fontSize: 11, color: tokens.colors.cyan, fontWeight: 700, textTransform: 'uppercase' }}>
                  Respuesta de la IA
                </Typography>
              </Box>
              {aiAnswer.loading ? (
                <Typography sx={{ fontSize: 13, color: tokens.colors.muted }}>Pensando…</Typography>
              ) : aiAnswer.error ? (
                <Typography sx={{ fontSize: 13, color: tokens.colors.red }}>{aiAnswer.error}</Typography>
              ) : (
                <Typography sx={{ fontSize: 13.5, color: tokens.colors.txt, lineHeight: 1.6 }}>{aiAnswer.text}</Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: tokens.colors.muted, fontSize: 13, textAlign: 'center', py: 3 }}>
              Sin resultados para "{query}" — presiona Enter para preguntarle a la IA
            </Typography>
          )
        ) : (
          <>
            <Box sx={{ fontSize: 11, color: tokens.colors.muted2, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', px: 1.25, pt: 1, pb: 0.5 }}>
              Preguntar a la IA
            </Box>
            {SUGGESTED_PROMPTS.map(prompt => (
              <Box
                key={prompt}
                onClick={() => { setQuery(prompt); handleAskAI(prompt); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
                  borderRadius: `${tokens.radius.md}px`, cursor: 'pointer', fontSize: 14,
                  color: tokens.colors.txt,
                  '&:hover': { backgroundColor: tokens.colors.panel2 },
                }}
              >
                <Box sx={{ width: 32, height: 32, borderRadius: `${tokens.radius.sm}px`, backgroundImage: tokens.gradient.soft, display: 'grid', placeItems: 'center', color: tokens.colors.txt }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                </Box>
                {prompt}
              </Box>
            ))}
            <Box sx={{ fontSize: 11, color: tokens.colors.muted2, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', px: 1.25, pt: 1, pb: 0.5 }}>
              Ir a
            </Box>
            {navItems.map(({ label, icon: Icon, action }) => (
              <Box
                key={label}
                onClick={action}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
                  borderRadius: `${tokens.radius.md}px`, cursor: 'pointer', fontSize: 14,
                  color: tokens.colors.txt,
                  '&:hover': { backgroundColor: tokens.colors.panel2 },
                }}
              >
                <Box sx={{
                  width: 32, height: 32, borderRadius: `${tokens.radius.sm}px`,
                  backgroundColor: tokens.colors.panel2, display: 'grid', placeItems: 'center', color: tokens.colors.cyan,
                }}>
                  <Icon sx={{ fontSize: 17 }} />
                </Box>
                {label}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default SearchPaletteModal;